import flatten from 'lodash/flatten';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import moment from 'utils/moment';
import type { Moment } from 'moment';
import { useMemo } from 'react';
import aptSelectors from 'services/selectors/apt';
import { ICalendarItemData, ICalendarSchema, ICalendarViewRowItem, ICalendarViewRowSpanInfoItem, IRowSpanInfo } from './types';
import getTimeLines, { setTime } from 'utils/getTimeLines';
import { momentTimezone } from 'utils/time';
import { dateParamSignal } from 'CalendarFilter/DatePicker';

export type ICalendarMappingResultValues = {
  rows: ICalendarViewRowItem[];
  rowsSpanInfo: ICalendarViewRowSpanInfoItem[];
  data: ICalendarItemData[]
};

const MINUTE_MS = 60000;

// One-time parse of an appointment's time strings. moment-timezone string parsing is expensive,
// so we do it once per order instead of thousands of times inside the row/column loops.
type IParsedOrder = {
  order: ICalendarItemData;
  id: string;
  idStr: string;
  start: Moment;        // start time in salon timezone
  startMs: number;      // start epoch ms
  endMinus1Ms: number;  // (end - 1 minute) epoch ms, matching the original `endTime - 1min` check
  dateKey: string;      // start day, MM-DD-YYYY
};

const useCalendarMapping = (schema: ICalendarSchema): ICalendarMappingResultValues => {
  // Stable underlying slices → memoized merge, so the heavy memos below only recompute
  // when appointments/block-hours actually change (not on every render).
  const appointments = aptSelectors.calendar.appointments();
  const blockHours = aptSelectors.calendar.blockHours();
  const data = useMemo(() => [...appointments, ...blockHours], [appointments, blockHours]);

  const parsedOrders = useMemo<IParsedOrder[]>(() => {
    return data.map(order => {
      const start = momentTimezone(order.startTime);
      const endMs = +momentTimezone(order.endTime);
      return {
        order,
        id: order.id,
        idStr: order.id.toString(),
        start,
        startMs: +start,
        endMinus1Ms: endMs - MINUTE_MS,
        dateKey: start.format('MM-DD-YYYY'),
      };
    });
  }, [data]);

  const rowTimesMaster = useMemo(() => {
    return getTimeLines(setTime(schema.timeHourStart, schema.timeMinuteStart), setTime(schema.timeHourEnd, schema.timeMinuteEnd), schema.distanceTimeline || 15);
  }, [schema.timeHourStart, schema.timeMinuteStart, schema.timeHourEnd, schema.timeMinuteEnd, schema.distanceTimeline]);

  // Orders on the selected day grouped by column. Reused by both the span scan and the row build,
  // replacing the previous per-cell `data.filter(...)`.
  const ordersByCol = useMemo(() => {
    const selectedDateKey = dateParamSignal.value?.format('MM-DD-YYYY');
    const dateMatched = parsedOrders.filter(po => po.dateKey === selectedDateKey);
    const map = new Map<string, IParsedOrder[]>();
    schema.headers.forEach(col => {
      const list = dateMatched.filter(po => schema.filterAppointments(po.order, col));
      if (list.length) map.set(col.id, list);
    });
    return map;
  }, [parsedOrders, schema.headers, schema.filterAppointments, dateParamSignal.value]);

  const rowsSpanInfo: ICalendarViewRowSpanInfoItem[] = useMemo(() => {
    const distance = schema.distanceTimeline;
    const rowsSpan = schema.headers.map(col => {
      const colOrders = ordersByCol.get(col.id);
      if (!colOrders || colOrders.length === 0) return undefined;

      // All of a column's orders share the selected day, so the slot wall-clock for a given
      // rowTime is the same for every order — derive it once per row from any order's date.
      const dateBase = colOrders[0].start;

      const mapping = rowTimesMaster.map(rowTime => {
        const obSetTime = { hour: rowTime.value.get('hour'), minute: rowTime.value.get('minute'), second: 0 };
        const slotMs = +dateBase.clone().set(obSetTime);
        const nextMs = slotMs + distance * MINUTE_MS;

        const orderList: string[] = [];
        colOrders.forEach(po => {
          const isBetween = po.startMs > slotMs && po.startMs < nextMs;            // start strictly inside the slot
          const isOrderTimeFinishValid = slotMs >= po.startMs && po.endMinus1Ms >= slotMs; // slot covered by the order
          if (isBetween || isOrderTimeFinishValid) orderList.push(po.idStr);
        });

        return ({
          orderList,
          rowId: rowTime.id,
          rowValue: rowTime.value,
        });
      });

      const spam: IRowSpanInfo[] = [];
      mapping.forEach((row, index) => {
        const prevOrders = get(mapping, [index - 1, 'orderList'], []) as string[];
        const orderList = row.orderList ?? [];
        if (orderList.length === 0) return;
        const isContinue = orderList.some(o => prevOrders.includes(o));
        if (!isContinue) {
          spam.push({
            rowStart: { id: row.rowId, value: row.rowValue.clone() },
            rowEnd: { id: row.rowId, value: row.rowValue.clone() },
            orderIds: [...row.orderList],
            rowId: row.rowId,
            colId: col.id,
            rowSpan: 1,
          });
        } else {
          const current = spam.find(o => !!orderList.find(k => o.orderIds.includes(k)));
          if (current) {
            current.orderIds = uniq([...current.orderIds, ...orderList]);
            current.rowEnd = { id: row.rowId, value: row.rowValue.clone() };
          }
        }
      });
      return spam;
    }).filter((o): o is IRowSpanInfo[] => !!o);

    const rowsSpanInfoTemp = flatten(rowsSpan) as IRowSpanInfo[];
    return rowsSpanInfoTemp.map(o => ({
      ...o,
      rowSpan: (o.rowEnd.value.diff(o.rowStart.value, 'minutes') / distance) + 1,
    }));
  }, [rowTimesMaster, schema.headers, schema.distanceTimeline, ordersByCol]);

  const rows: ICalendarViewRowItem[] = useMemo(() => {
    // O(1) lookups instead of `.find()` / `.filter()` over rowsSpanInfo per cell.
    const spanByRowCol = new Map<string, ICalendarViewRowSpanInfoItem>();
    const spanByCol = new Map<string, ICalendarViewRowSpanInfoItem[]>();
    rowsSpanInfo.forEach(o => {
      spanByRowCol.set(o.rowId + '|' + o.colId, o);
      const list = spanByCol.get(o.colId);
      if (list) list.push(o); else spanByCol.set(o.colId, [o]);
    });

    const dayBase = dateParamSignal.value ? moment(dateParamSignal.value.format()) : moment();

    return rowTimesMaster.map((rowTime) => {
      const obSetTime = { hour: rowTime.value.get('hour'), minute: rowTime.value.get('minute'), second: 0 };

      const colData = schema.headers.map((col) => {
        const colOrders = ordersByCol.get(col.id);
        const rowInfo = spanByRowCol.get(rowTime.id + '|' + col.id);
        const orderIds = rowInfo?.orderIds;
        const bookingDataFilter = (orderIds && colOrders)
          ? colOrders.filter(po => orderIds.includes(po.idStr)).map(po => po.order)
          : [];

        let rowTimeValue: Moment;
        if (col.type === 'date') {
          rowTimeValue = (col.value as Moment)?.clone().set(obSetTime);
        } else {
          rowTimeValue = dayBase.clone().set(obSetTime);
        }

        return ({
          id: col.id,
          data: bookingDataFilter,
          rowTime: rowTimeValue,
          nextRowTime: rowTimeValue.clone().add(schema.distanceTimeline, 'minute').format('hh-mm-A'),
          headerData: col,
        });
      }).filter((col) => {
        const spanList = spanByCol.get(col.id);
        if (!spanList) return true;
        return !spanList.some(o => o.rowSpan > 1 && (
          rowTime.value.isBetween(o.rowStart.value, o.rowEnd.value) ||
          rowTime.value.isSame(o.rowEnd.value)
        ));
      });

      return {
        id: rowTime.id,
        rowTime: rowTime.value,
        nextRowTime: rowTime.value.clone().add(schema.distanceTimeline, 'minute').format('hh-mm-A'),
        colData,
      };
    });
  }, [rowTimesMaster, rowsSpanInfo, schema.headers, schema.distanceTimeline, ordersByCol, dateParamSignal.value]);

  return useMemo(() => ({
    rows,
    rowsSpanInfo,
    data,
  }), [rows, rowsSpanInfo, data]);
};

export default useCalendarMapping;
